import React, { useEffect, useState, useRef } from 'react';
import { MessageSquareIcon, HandIcon, BarChartIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const availableFeatures = [{
  id: 1,
  name: 'Reframe',
  subheading: 'About to text something bold or heated?',
  description: 'Emigo helps you fine-tune your tone: charming when dating, calm in relationships, and professional at work.',
  iconUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/sdNfGzwjcXeJDGddhB9ZJ8/image.png',
  gradient: 'from-[#E87B5C] to-[#FFD700]',
  bgColor: 'bg-gradient-to-br from-white to-[#FFF5EB]'
}, {
  id: 2,
  name: 'Interpret',
  subheading: 'Not sure what they really meant?',
  description: 'Emigo helps you read the tone and intent, and suggests how to respond thoughtfully, not react emotionally.',
  iconUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5ER278KwVKNoQE1xgrmbJ9/image.png',
  gradient: 'from-[#E87B5C] to-[#FFD700]',
  bgColor: 'bg-gradient-to-br from-white to-[#FFF5EB]'
}, {
  id: 3,
  name: 'Create',
  subheading: 'Stuck on what to say?',
  description: "Emigo helps you craft clear, thoughtful messages; whether you're trying to make a good impression, reconnect, or smooth things over.",
  iconUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/iqQTwJrEnvKn9hxoBkYxiy/image.png',
  gradient: 'from-[#E87B5C] to-[#FFD700]',
  bgColor: 'bg-gradient-to-br from-white to-[#FFF5EB]'
}];
const comingSoonFeatures = [{
  id: 4,
  name: 'Analytics',
  subheading: 'Want to understand your communication patterns?',
  description: 'Emigo tracks your emotional trends over time, helping you see progress and identify triggers.',
  icon: BarChartIcon,
  gradient: 'from-[#E87B5C] to-[#FFD700]',
  bgColor: 'bg-gradient-to-br from-white to-[#FFF5EB]'
}, {
  id: 5,
  name: 'Pause',
  subheading: 'Feeling too emotional to text safely?',
  description: 'Emigo can temporarily lock your keyboard when emotions run too high, preventing regrettable messages.',
  icon: HandIcon,
  gradient: 'from-[#E87B5C] to-[#FFD700]',
  bgColor: 'bg-gradient-to-br from-white to-[#FFF5EB]'
}, {
  id: 6,
  name: 'Unpack',
  subheading: 'Need to process a difficult conversation?',
  description: 'Emigo helps you unpack complex exchanges and understand what happened beneath the surface.',
  icon: MessageSquareIcon,
  gradient: 'from-[#E87B5C] to-[#FFD700]',
  bgColor: 'bg-gradient-to-br from-white to-[#FFF5EB]'
}];
const FeatureCard = ({
  feature
}) => {
  const [isHovered, setIsHovered] = useState(false);
  return <div className={`group relative rounded-2xl ${feature.bgColor} transition-all duration-300 ease-out shadow-lg hover:shadow-2xl h-full flex flex-col overflow-hidden border-2 border-transparent hover:border-[#E87B5C]`} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-95">
      {/* Decorative corner accent */}
      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-[#E87B5C]/10 to-transparent rounded-bl-full opacity-50" data-id="element-96"></div>

      <div className="relative p-6 sm:p-7 md:p-8 flex flex-col h-full" data-id="element-97">
        {/* Icon and Title - Side by side */}
        <div className="flex items-center gap-4 mb-5" data-id="element-98">
          {feature.iconUrl ? <div className="w-16 h-16 sm:w-18 sm:h-18 rounded-2xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 shadow-lg flex-shrink-0 ring-2 ring-white" style={{
          backgroundColor: '#E87B5C',
          backgroundImage: `url(${feature.iconUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }} data-id="element-99"></div> : <div className="w-16 h-16 sm:w-18 sm:h-18 rounded-2xl bg-gradient-to-br from-[#E87B5C] to-[#FFD700] flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 shadow-lg flex-shrink-0 ring-2 ring-white" data-id="element-100">
              <feature.icon className="h-9 w-9 sm:h-10 sm:w-10 text-white" data-id="element-101" />
            </div>}

          {/* Title */}
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 group-hover:text-[#E87B5C] transition-colors duration-300" data-id="element-102">
            {feature.name}
          </h3>
        </div>

        {/* Subheading */}
        <p className={`text-base sm:text-lg font-semibold mb-4 transition-colors duration-300 ${isHovered ? 'text-[#D16A4A]' : 'text-[#D16A4A]/80'}`} data-id="element-103">
          {feature.subheading}
        </p>

        {/* Description */}
        <p className="text-sm sm:text-base text-gray-700 leading-relaxed flex-grow" data-id="element-104">
          {feature.description}
        </p>
      </div>
    </div>;
};
const ComingSoonCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const totalSlides = comingSoonFeatures.length + 1;
  const nextSlide = () => {
    setCurrentSlide(prev => prev === totalSlides - 1 ? 0 : prev + 1);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => prev === 0 ? totalSlides - 1 : prev - 1);
  };
  return <div className="relative rounded-2xl bg-gradient-to-br from-white to-[#FFF5EB] shadow-lg overflow-visible h-full border-2 border-transparent hover:border-[#E87B5C] transition-all duration-300" data-id="element-105">
      {/* Decorative corner accent */}
      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-[#E87B5C]/10 to-transparent rounded-bl-full opacity-50" data-id="element-106"></div>

      <div className="overflow-hidden h-full rounded-2xl" data-id="element-107">
        <div className="flex transition-transform duration-500 ease-out h-full" style={{
        transform: `translateX(-${currentSlide * 100}%)`
      }} data-id="element-108">
          {/* First slide: Coming Soon */}
          <div className="w-full flex-shrink-0 p-6 sm:p-8 flex flex-col items-center justify-center h-full" data-id="element-109">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#E87B5C] to-[#FFD700] flex items-center justify-center mb-4 shadow-lg ring-2 ring-white" data-id="element-110">
              <span className="text-2xl" data-id="element-111">✨</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-[#E87B5C] mb-4 text-center" data-id="element-112">
              Coming Soon
            </h3>
            <p className="text-sm sm:text-base text-gray-700 text-center font-medium" data-id="element-113">
              More powerful features are on the way! Swipe to preview what's
              coming.
            </p>
          </div>
          {/* Feature slides */}
          {comingSoonFeatures.map(feature => <div key={feature.id} className="w-full flex-shrink-0 p-6 sm:p-8 h-full" data-id="element-114">
              <div className="flex flex-col h-full" data-id="element-115">
                {/* Icon and Title - Side by side */}
                <div className="flex items-center gap-4 mb-5" data-id="element-116">
                  <div className={`h-16 w-16 sm:h-18 sm:w-18 rounded-2xl bg-gradient-to-br ${feature.gradient} text-white flex items-center justify-center shadow-lg flex-shrink-0 ring-2 ring-white`} data-id="element-117">
                    <feature.icon className="h-9 w-9 sm:h-10 sm:w-10" data-id="element-118" />
                  </div>

                  {/* Title */}
                  <div data-id="element-119">
                    <h3 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-120">
                      {feature.name}
                    </h3>
                  </div>
                </div>

                {/* Coming Soon badge */}
                <span className="inline-block px-3 py-1.5 text-xs font-bold text-white bg-gradient-to-r from-[#E87B5C] to-[#FFD700] rounded-full mb-3 self-start shadow-md" data-id="element-121">
                  Coming Soon
                </span>

                {/* Subheading */}
                <p className="text-base sm:text-lg font-semibold text-[#D16A4A]/80 mb-4" data-id="element-122">
                  {feature.subheading}
                </p>

                {/* Description */}
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed flex-grow" data-id="element-123">
                  {feature.description}
                </p>
              </div>
            </div>)}
        </div>
      </div>
      {/* Navigation buttons */}
      <button onClick={prevSlide} className="absolute top-1/2 -left-4 -translate-y-1/2 bg-white rounded-full p-2.5 shadow-xl z-10 text-[#E87B5C] border-2 border-[#E87B5C]/20 hover:border-[#E87B5C] hover:shadow-2xl transition-all duration-200 hover:scale-110" aria-label="Previous slide" data-id="element-124">
        <ChevronLeftIcon className="h-5 w-5" data-id="element-125" />
      </button>
      <button onClick={nextSlide} className="absolute top-1/2 -right-4 -translate-y-1/2 bg-white rounded-full p-2.5 shadow-xl z-10 text-[#E87B5C] border-2 border-[#E87B5C]/20 hover:border-[#E87B5C] hover:shadow-2xl transition-all duration-200 hover:scale-110" aria-label="Next slide" data-id="element-126">
        <ChevronRightIcon className="h-5 w-5" data-id="element-127" />
      </button>
      {/* Indicator dots */}
      <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2" data-id="element-128">
        {Array.from({
        length: totalSlides
      }).map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`h-2.5 rounded-full transition-all duration-300 shadow-sm ${currentSlide === index ? 'bg-gradient-to-r from-[#E87B5C] to-[#FFD700] w-8' : 'bg-gray-400 w-2.5'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-129"></button>)}
      </div>
    </div>;
};
const FeaturesSection = () => {
  const featureRefs = useRef([]);
  const [visibleFeatures, setVisibleFeatures] = useState({});
  const sectionRef = useRef(null);
  // Intersection observer for animation on scroll
  useEffect(() => {
    featureRefs.current = featureRefs.current.slice(0, availableFeatures.length + 1);
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3
    };
    const observerCallback = entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const featureIndex = parseInt(entry.target.dataset.index);
          setVisibleFeatures(prev => ({
            ...prev,
            [featureIndex]: true
          }));
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    featureRefs.current.forEach(ref => {
      if (ref) observer.observe(ref);
    });
    return () => {
      featureRefs.current.forEach(ref => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);
  return <section id="features" ref={sectionRef} className="relative py-24 overflow-hidden bg-[#FFF5EB]" data-id="element-130">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-131">
        <div className="text-center mb-16" data-id="element-132">
          <h2 className="text-4xl md:text-5xl font-bold text-[#E87B5C]" data-id="element-133">
            Here is How Emigo can Help!
          </h2>
        </div>
        {/* Features grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8" data-id="element-134">
          {availableFeatures.map((feature, index) => <div key={feature.id} ref={el => featureRefs.current[index] = el} data-index={index} className={`transition-all duration-500 ${visibleFeatures[index] ? 'opacity-100 transform-none' : 'opacity-0 scale-95'}`} style={{
          transitionDelay: `${index * 150}ms`,
          transitionProperty: 'transform, opacity, scale'
        }} data-id="element-135">
              <FeatureCard feature={feature} data-id="element-136" />
            </div>)}
          {/* Coming Soon Carousel */}
          <div ref={el => featureRefs.current[availableFeatures.length] = el} data-index={availableFeatures.length} className={`transition-all duration-500 ${visibleFeatures[availableFeatures.length] ? 'opacity-100 transform-none' : 'opacity-0 scale-95'}`} style={{
          transitionDelay: `${availableFeatures.length * 150}ms`,
          transitionProperty: 'transform, opacity, scale'
        }} data-id="element-137">
            <ComingSoonCarousel data-id="element-138" />
          </div>
        </div>
      </div>
    </section>;
};
export default FeaturesSection;